import { UPLOAD_STATUS } from './consts';
import Services, { UploadUtil } from './sdkServices';

const { UPLOADING, UPLOAD_SUCCESS, PROCESSING, PROCESS_FAILED, UPLOAD_FAILED } =
  UPLOAD_STATUS;

const isCommandCommitted = (result) =>
  result?.data.type === 'commands' &&
  ['completed', 'committed'].includes(result?.data.attributes.status);

const upload = async (
  projectId,
  file,
  { uploadStatusHandler, checkFileRemoved },
) => {
  try {
    const result = await Services.Upload.uploadFile(
      projectId,
      file.parentFolderUrn,
      file.ref,
      {
        isAborted: () => checkFileRemoved(file),
        onProgress: (progress) =>
          uploadStatusHandler({
            ...file,
            progress,
            status: UPLOADING,
          }),
        onUploaded: () => {
          uploadStatusHandler({
            ...file,
            status: UPLOAD_SUCCESS,
          });
        },
      },
    );

    if (isCommandCommitted(result)) {
      // single file upload command returns storageUrn property while multiple file upload command does not return
      const { storageUrn } =
        result.data.attributes.created.find(({ type }) => type === 'versions')
          ?.data || {};
      file.status = PROCESSING;
      file.storageUrn = storageUrn;
      file.result = result;
    } else {
      file.progress = 0;
      file.status = PROCESS_FAILED;
    }
  } catch {
    file.progress = 0;
    file.status = UPLOAD_FAILED;
  }

  uploadStatusHandler(file);
};

/**
 *  UploadWorker handles the scenario of uploading large count of file.
 *  It only cares about upload itself. both success and fail are `resolved`,
 *  Once resolved, upload files are removed, result will be handed by callback.
 *  In failed case, callback updates status.
 */
export class UploadWorker {
  constructor(projectId, files, handlers, threadPoolSize = 6) {
    this.threadPoolSize = threadPoolSize;
    this.projectId = projectId;
    this.buffer = [...files];
    this.handlers = handlers;
    this.threadCount = 0;
    this.start();
  }

  async start(offset = this.threadPoolSize) {
    const chunk = this.buffer.slice(0, offset);
    this.buffer = this.buffer.slice(offset);
    chunk.forEach(async (file) => {
      ++this.threadCount;
      await upload(this.projectId, file, this.handlers);
      --this.threadCount;
      this.start(1);
    });
  }

  addFiles(files) {
    this.buffer = [...this.buffer, ...files];
    const availableThread = this.threadPoolSize - this.threadCount;
    availableThread > 0 && this.start(availableThread);
  }

  removeFile(target) {
    this.buffer = this.buffer.filter((file) => file.id !== target.id);
  }
}

export const getUploadFileEntity = (data, parentFolderUrn, isRfiUpload) => {
  const version =
    data.data.attributes.created.find(({ type }) => type === 'versions')
      ?.data || {};
  return {
    id: version.lineageUrn,
    urn: version.lineageUrn,
    name: version.name,
    parentFolderUrn,
    isRfiUpload,
  };
};

export const validateFiles = async (files) => {
  let validFiles = [];
  let invalidFiles = [];

  await Promise.all(
    files.map(async (file) => {
      try {
        const { code: errorCode } = await UploadUtil.validateFile(file);
        if (errorCode) {
          // Safe: no await between read and write, so no interleaving risk in JS's single-threaded model
          invalidFiles = [...invalidFiles, file];
        } else {
          validFiles = [...validFiles, file];
        }
      } catch {
        invalidFiles = [...invalidFiles, file];
      }
    }),
  );

  return [validFiles, invalidFiles];
};

export const isRfiSource = (sourceEntityType) => sourceEntityType === 'rfi';
